'use client';

import { StyledComponent } from '@emotion/styled';
import X from '@mds/icons/X';
import { DialogProps, IconButton, Modal, ModalProps, Paper, PaperProps, styled } from '@mui/material';
import { createContext, forwardRef, MouseEvent, useCallback, useContext, useMemo } from 'react';

interface CloseDialogContextValue {
  onClose?: DialogProps['onClose'];
}

const CloseDialogContext = createContext<CloseDialogContextValue>({});

if (process.env.NODE_ENV !== 'production') {
  CloseDialogContext.displayName = 'CloseDialogContext';
}

export const closeIconClassName = 'MdsModalCloseIcon';

export function CloseButton() {
  const { onClose } = useContext(CloseDialogContext);
  const handleClose = useCallback(
    (event: MouseEvent) => {
      // We cannot redefine the type of MUI's onClose reason here to add 'closeIconClick'.
      // An alternative would be exposing a new Dialog component for MDS, however the close
      // reason is not commonly used.
      onClose?.(event, 'closeIconClick' as unknown as 'backdropClick');
    },
    [onClose],
  );
  return (
    <IconButton
      aria-label="close"
      onClick={handleClose}
      size="small"
      className={closeIconClassName}
      sx={(theme) => ({
        position: 'absolute',
        right: theme.spacing(3),
        top: theme.spacing(3),
      })}
    >
      <X fontSize="inherit" />
    </IconButton>
  );
}

// Adapted from MUI source
const StyledDialogRoot = styled(Modal, {
  name: 'MuiDialog',
  shouldForwardProp: (prop) => prop !== 'disableCloseIcon',
  overridesResolver: (props, styles) => styles.root,
})({
  '@media print': {
    // Use !important to override the Modal inline-style.
    position: 'absolute !important',
  },
  variants: [
    {
      props: { disableCloseIcon: true },
      style: {
        [`& .${closeIconClassName}`]: {
          display: 'none',
        },
      },
    },
  ],
  // TS complains if the type annotation is not explicit
}) as StyledComponent<ModalProps>;

/**
 * Encapsulates Modal in <CloseDialogContext> to allow contents to render the close button
 * arbitrarily
 */
export const DialogRoot = forwardRef<HTMLDivElement, ModalProps>(function DialogRoot(props, ref) {
  const { onClose } = props;
  const contextValue = useMemo(() => ({ onClose }), [onClose]);
  return (
    // eslint-disable-next-line react/no-context-provider
    <CloseDialogContext.Provider value={contextValue}>
      <StyledDialogRoot {...props} ref={ref} />
    </CloseDialogContext.Provider>
  );
});

/**
 * Adds a close button to dialogs
 */

export const DialogPaper = forwardRef<HTMLDivElement, PaperProps>(function DialogPaper(props, ref) {
  const { children, ...rest } = props;
  return (
    <Paper {...rest} ref={ref}>
      <CloseButton />
      {children}
    </Paper>
  );
});
